/**
 * Base de datos local del POS (IndexedDB con Dexie).
 *
 * Tres almacenes con propósitos muy distintos:
 *
 * * `catalog` — copia del catálogo para poder **vender sin red**. Es lo único
 *   que baja del servidor; el POS nunca lo modifica, así que no hay conflictos
 *   que resolver.
 * * `outbox` — ventas y devoluciones que todavía no llegaron al servidor. Es
 *   `append-only`: se encolan, se intentan enviar y se marcan como enviadas, pero
 *   nunca se reescriben ni se borran hasta que el servidor confirma.
 * * `meta` — datos pequeños: sucursal activa, turno abierto, última
 *   sincronización del catálogo.
 */

import Dexie, { type Table } from "dexie";

import type { components } from "../api-types";

type Schemas = components["schemas"];
export type SaleCreate = Schemas["SaleCreate"];
export type SalePublic = Schemas["SalePublic"];
export type RefundCreate = Schemas["RefundCreate"];
export type RefundPublic = Schemas["RefundPublic"];
export type ResolvedPrice = Schemas["ResolvedPrice"];
export type BranchPublic = Schemas["BranchPublic"];
export type CashTotalsPublic = Schemas["CashTotalsPublic"];
export type SessionPublic = Schemas["SessionPublic"];

/** Producto cacheado para buscar sin red. */
export interface CachedProduct {
  variantId: string;
  productId: string;
  sku: string;
  name: string;
  productName: string;
  kind: string;
  barcode: string | null;
  /** Precio de la lista del negocio, en centavos. `null` = sin precio. */
  priceCents: number | null;
  tracked: boolean;
  taxRate: string;
  /** Nombre normalizado (sin acentos, minúsculas) para buscar offline. */
  searchText: string;
}

export type OutboxKind = "sale" | "refund" | "void";

export type OutboxStatus = "pendiente" | "enviando" | "enviada" | "rechazada";

export interface OutboxEntry {
  /** Identificador local, también visible en la interfaz mientras no sincroniza. */
  id: string;
  kind: OutboxKind;
  /** Cuerpo tal cual se mandará al servidor. */
  payload: SaleCreate | RefundCreate | { sale_id: string; reason: string };
  /** La clave que hace que reintentar no cobre dos veces. */
  idempotencyKey: string;
  /** Número local que se muestra en el ticket mientras no hay folio real. */
  localNumber: number;
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  lastError: string | null;
  /** Respuesta del servidor cuando ya se sincronizó. */
  response: SalePublic | RefundPublic | null;
  /** Ruta a la que se envía. */
  path: string;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

class PosDatabase extends Dexie {
  catalog!: Table<CachedProduct, string>;
  outbox!: Table<OutboxEntry, string>;
  meta!: Table<MetaEntry, string>;

  constructor() {
    super("megasyr-pos");
    this.version(1).stores({
      catalog: "variantId, barcode, sku, kind, productName",
      outbox: "id, status, createdAt, kind",
      meta: "key",
    });
  }
}

export const db = new PosDatabase();

// ---------------------------------------------------------------------------
// Metadatos
// ---------------------------------------------------------------------------

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

/** Normaliza para buscar: sin acentos, en minúsculas. */
export function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export async function replaceCatalog(products: CachedProduct[], tenantId?: string): Promise<void> {
  await db.transaction("rw", db.catalog, async () => {
    await db.catalog.clear();
    await db.catalog.bulkPut(products);
  });
  await setMeta("catalog.syncedAt", Date.now());
  if (tenantId) {
    await setMeta("catalog.tenantId", tenantId);
  }
}

/**
 * Limpia el catálogo local y la sucursal recordada cuando cambia el negocio
 * activo o se cierra sesión, evitando que un negocio vea datos de otro.
 */
export async function clearTenantLocalCache(): Promise<void> {
  try {
    await db.transaction("rw", db.catalog, db.meta, async () => {
      await db.catalog.clear();
      await db.meta.delete("catalog.syncedAt");
      await db.meta.delete("catalog.tenantId");
      await db.meta.delete("pos.branchId");
    });
  } catch {
    // Ignorar en entornos de prueba sin IndexedDB
  }
}

/**
 * Búsqueda local, con las mismas reglas que la del servidor: primero el código
 * de barras, luego el SKU y después el nombre sin acentos.
 */
export async function searchCatalog(term: string, limit = 30): Promise<CachedProduct[]> {
  const query = term.trim();
  if (!query) return [];

  const byBarcode = await db.catalog.where("barcode").equals(query).limit(1).toArray();
  if (byBarcode.length > 0) return byBarcode;

  const bySku = await db.catalog
    .filter((item) => normalize(item.sku).includes(normalize(query)))
    .limit(limit)
    .toArray();
  if (bySku.length > 0) return bySku;

  const needle = normalize(query);
  return db.catalog
    .filter(
      (item) =>
        item.searchText.includes(needle) || normalize(item.productName).includes(needle),
    )
    .limit(limit)
    .toArray();
}

// ---------------------------------------------------------------------------
// Cola de salida
// ---------------------------------------------------------------------------

export async function enqueue(
  entry: Omit<OutboxEntry, "status" | "attempts" | "createdAt" | "lastError" | "response">,
): Promise<OutboxEntry> {
  const row: OutboxEntry = {
    ...entry,
    status: "pendiente",
    attempts: 0,
    createdAt: Date.now(),
    lastError: null,
    response: null,
  };
  await db.outbox.put(row);
  return row;
}

export async function pendingEntries(): Promise<OutboxEntry[]> {
  return db.outbox.where("status").anyOf("pendiente", "enviando").sortBy("createdAt");
}

export async function outboxSummary(): Promise<{ pending: number; rejected: number }> {
  const [pending, rejected] = await Promise.all([
    db.outbox.where("status").anyOf("pendiente", "enviando").count(),
    db.outbox.where("status").equals("rechazada").count(),
  ]);
  return { pending, rejected };
}

export async function nextLocalNumber(): Promise<number> {
  const current = (await getMeta<number>("outbox.localNumber")) ?? 0;
  const next = current + 1;
  await setMeta("outbox.localNumber", next);
  return next;
}
